import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { resetPasswordApi } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_9e7963b6-8d0a-418d-8246-af7a5b4f8387/artifacts/h6dgewwm_image.png';

export default function ResetPasswordPage() {
  const { t } = useApp();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ password: '', confirm: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error(t('resetPw.minLength'));
      return;
    }
    if (form.password !== form.confirm) {
      toast.error(t('resetPw.mismatch'));
      return;
    }
    setLoading(true);
    try {
      await resetPasswordApi({ token, new_password: form.password });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('resetPw.invalidToken'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="LUXOR TEK" className="h-12 mx-auto mb-6" />
          <div className="card-dark p-8">
            <p className="text-red-400 mb-4">{t('resetPw.invalidToken')}</p>
            <Link to="/buyer/login" className="btn-primary inline-block">{t('common.backToLogin')}</Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="LUXOR TEK" className="h-12 mx-auto mb-6" />
          <div className="card-dark p-8">
            <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" data-testid="reset-success-title">{t('resetPw.success')}</h2>
            <Link to="/buyer/login" className="btn-primary inline-block" data-testid="go-to-login-btn">{t('common.backToLogin')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="reset-password-page">
      <Toaster position="top-right" theme="dark" />
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <img src={LOGO_URL} alt="LUXOR TEK" className="h-12 mx-auto mb-6" />
          <h1 className="text-2xl font-bold">{t('resetPw.title')}</h1>
          <p className="text-white/60 mt-2">{t('resetPw.subtitle')}</p>
        </div>
        <div className="card-dark p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('resetPw.newPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                  placeholder="********"
                  data-testid="reset-new-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-white/60 text-sm mb-2">{t('resetPw.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className="w-full pl-12 pr-4 py-3 bg-[#050505] border border-white/10 focus:border-[#00D4FF]"
                  placeholder="********"
                  data-testid="reset-confirm-password"
                />
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-btn">
              {loading ? t('resetPw.resetting') : t('resetPw.resetBtn')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
